<template>
  <article class="journey-place" :dir="isEnglish ? 'ltr' : 'rtl'">
    <div :class="['journey-place-cover', { 'journey-place-cover-empty': !showImage }]">
      <img
        v-if="showImage"
        :src="newAttractionImage"
        :alt="newAttractionTitle"
        class="journey-place-photo"
        @error="imageBroken = true"
      >
      <span v-else class="journey-place-initial" aria-hidden="true">{{ initial }}</span>
      <span v-if="showRating" class="journey-place-rating">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
          <path fill="currentColor" d="m12 17.3 6.2 3.7-1.6-7 5.4-4.7-7.2-.6L12 2 9.2 8.7 2 9.3l5.4 4.7-1.6 7z"/>
        </svg>
        {{ ratingLabel }}
      </span>
    </div>

    <div class="journey-place-body">
      <h3 class="journey-place-title">{{ newAttractionTitle }}</h3>
      <p v-if="newAttractionAddress" class="journey-place-address">{{ newAttractionAddress }}</p>

      <p v-if="newAttractionText" class="journey-place-text usingNewLine" v-html="newAttractionText"></p>

      <div v-if="newAttractionLink || newAttractionVoucherUrl" class="journey-place-actions">
        <a
          v-if="newAttractionLink"
          :href="newAttractionLink"
          target="_blank"
          rel="noopener"
          class="journey-place-action journey-place-action-main"
        >
          <span class="material-icons" aria-hidden="true">open_in_new</span>
          {{ t('viewPlace') }}
        </a>
        <a
          v-if="newAttractionVoucherUrl"
          :href="newAttractionVoucherUrl"
          target="_blank"
          rel="noopener"
          class="journey-place-action"
        >
          <span class="material-icons" aria-hidden="true">receipt_long</span>
          {{ t('voucher') }}
        </a>
      </div>

      <section v-if="newAttractionVideoUrl || newAttractionAudioUrl" class="journey-place-section">
        <div v-if="newAttractionVideoUrl" class="journey-place-media">
          <h4 class="journey-place-heading">{{ t('video') }}</h4>
          <video controls preload="none" class="journey-place-video" :src="newAttractionVideoUrl">
            {{ t('videoTagNotSupported') }}
          </video>
        </div>
        <div v-if="newAttractionAudioUrl" class="journey-place-media">
          <h4 class="journey-place-heading">{{ t('audio') }}</h4>
          <audio controls class="journey-place-audio" :src="newAttractionAudioUrl">
            {{ t('audioTagNotSupported') }}
          </audio>
        </div>
      </section>

      <section v-if="hasOpeningHours" class="journey-place-section journey-place-hours">
        <h4 class="journey-place-heading">
          <span class="material-icons" aria-hidden="true">schedule</span>
          {{ t('openingHours') }}
        </h4>
        <ul class="journey-place-hours-list">
          <li v-for="(line, index) in newAttractionOpeningHours" :key="index">
            <template v-if="splitHours(line)">
              <span class="journey-place-hours-day">{{ splitHours(line).day }}</span>
              <span class="journey-place-hours-time"><bdi>{{ splitHours(line).time }}</bdi></span>
            </template>
            <span v-else>{{ line }}</span>
          </li>
        </ul>
      </section>

      <section v-if="isNewAttractionReview && !isSubdomain && reviews.length" class="journey-place-section">
        <h4 class="journey-place-heading">
          <span class="material-icons" aria-hidden="true">forum</span>
          {{ t('placeReviews') }}
        </h4>
        <div class="journey-place-reviews">
          <figure v-for="(review, index) in reviews" :key="index" class="journey-place-review">
            <figcaption class="journey-place-review-head">
              <span class="journey-place-avatar" aria-hidden="true">{{ firstLetter(review.author_name) }}</span>
              <span class="journey-place-review-name">{{ review.author_name }}</span>
              <span v-if="review.rating" class="journey-place-review-stars" :aria-label="String(review.rating)">
                <span v-for="n in 5" :key="n" :class="{ 'journey-star-off': n > Math.round(review.rating) }">★</span>
              </span>
            </figcaption>
            <blockquote v-if="review.text" class="journey-place-review-text">{{ review.text }}</blockquote>
          </figure>
        </div>
      </section>
    </div>
  </article>
</template>

<script>
import { mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";

/**
 * The journey design's attraction details (the card's "i" button): the
 * place's photo across the top with its rating on it, the title and the
 * description, the link and voucher as pill buttons, then the opening hours
 * as a table and the reviews as cards. The same store data as the classic
 * popup (src/components/Modals/Attraction.vue), which shows this instead of
 * its own content when the trip page is in the journey design.
 */
export default {
  name: 'JourneyAttractionDetails',
  mixins: [translationMixin],
  data() {
    return { imageBroken: false };
  },
  computed: {
    ...mapGetters([
      'newAttractionTitle',
      'newAttractionAddress',
      'newAttractionText',
      'newAttractionLink',
      'newAttractionImage',
      'newAttractionVideoUrl',
      'newAttractionAudioUrl',
      'newAttractionVoucherUrl',
      'newAttractionOpeningHours',
      'newAttractionReview',
      'newAttractionRate',
      'isNewAttractionRate',
      'isNewAttractionReview',
    ]),
    ...mapGetters('theme', ['isSubdomain']),
    showImage() {
      return !!this.newAttractionImage && !this.imageBroken;
    },
    showRating() {
      return this.isNewAttractionRate && !this.isSubdomain && Number(this.newAttractionRate) > 0;
    },
    ratingLabel() {
      return Number(this.newAttractionRate).toFixed(1);
    },
    initial() {
      return this.firstLetter(this.newAttractionTitle);
    },
    hasOpeningHours() {
      return Array.isArray(this.newAttractionOpeningHours) && this.newAttractionOpeningHours.length > 0;
    },
    reviews() {
      return Array.isArray(this.newAttractionReview) ? this.newAttractionReview : [];
    },
  },
  watch: {
    newAttractionImage() {
      this.imageBroken = false;
    },
  },
  methods: {
    firstLetter(text) {
      return (text || '').trim().charAt(0).toUpperCase();
    },
    // "Monday: 9:00 AM – 5:00 PM" -> day and time in two columns; a line
    // without a colon after the day stays one line.
    splitHours(line) {
      const match = /^([^:\d]+):\s*(.+)$/.exec(String(line || ''));
      return match ? { day: match[1].trim(), time: match[2].trim() } : null;
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.journey-place {
  --journey-ink: #0d1117;
  --journey-muted: #6e6e73;
  --journey-tint: rgba(7, 160, 195, 0.1);
  --journey-tint: color-mix(in srgb, var(--primary-color, #07a0c3) 11%, #fff);

  color: var(--journey-ink);
  text-align: start;
}

.journey-place-cover {
  position: relative;
  aspect-ratio: 16 / 9;
  max-height: 46vh;
  width: 100%;
  overflow: hidden;
  background: #1b1f27;
}

.journey-place-cover::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(8, 12, 20, 0.35) 0%, rgba(8, 12, 20, 0) 30%);
  pointer-events: none;
}

.journey-place-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: journey-place-zoom 1.2s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.journey-place-cover-empty {
  aspect-ratio: 16 / 6;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(120% 90% at 20% 0%, rgba(255, 255, 255, 0.25), transparent 60%),
    var(--primary-background, #07a0c3);
}

.journey-place-initial {
  font-size: 84px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.85);
}

.journey-place-rating {
  position: absolute;
  z-index: 1;
  bottom: 16px;
  inset-inline-start: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 20px -10px rgba(0, 0, 0, 0.5);
  font-size: 15px;
  font-weight: 800;
  color: var(--journey-ink);
}

.journey-place-rating svg {
  color: #f5b301;
}

.journey-place-body {
  padding: clamp(20px, 4vw, 32px) clamp(18px, 4vw, 36px) clamp(24px, 4vw, 36px);
}

.journey-place-title {
  margin: 0;
  font-size: clamp(24px, 5vw, 32px);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: var(--journey-ink);
  overflow-wrap: anywhere;
}

.journey-place-address {
  margin: 8px 0 0;
  font-size: 15px;
  color: var(--journey-muted);
}

.journey-place-text {
  margin: 18px 0 0;
  font-size: 17px;
  line-height: 1.75;
  letter-spacing: normal;
  color: #2b2f36;
}

.journey-place-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 24px;
}

.journey-place-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 22px;
  border-radius: 999px;
  background: var(--journey-tint);
  color: var(--primary-color, #07a0c3);
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
  transition: box-shadow 0.2s ease, filter 0.2s ease;
}

.journey-place-action:hover {
  text-decoration: none;
  color: var(--primary-color, #07a0c3);
  filter: brightness(0.97);
}

.journey-place-action-main,
.journey-place-action-main:hover {
  background: var(--primary-background, #07a0c3);
  color: var(--primary-text-color, #fff);
  box-shadow: 0 12px 24px -14px rgba(0, 0, 0, 0.55);
}

.journey-place-action .material-icons {
  font-size: 19px;
}

.journey-place-section {
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #ececef;
}

.journey-place-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  font-size: 19px;
  font-weight: 800;
  color: var(--journey-ink);
}

.journey-place-heading .material-icons {
  font-size: 22px;
  color: var(--primary-color, #07a0c3);
}

.journey-place-media + .journey-place-media {
  margin-top: 16px;
}

.journey-place-video {
  width: 100%;
  max-height: 320px;
  border-radius: 18px;
  background: #000;
}

.journey-place-audio {
  width: 100%;
}

.journey-place-hours-list {
  margin: 0;
  padding: 6px 18px;
  list-style: none;
  border-radius: 18px;
  background: var(--journey-tint);
}

.journey-place-hours-list li {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;
  font-size: 15px;
  color: #2b2f36;
}

.journey-place-hours-list li + li {
  border-top: 1px solid rgba(255, 255, 255, 0.8);
}

.journey-place-hours-day {
  font-weight: 700;
}

.journey-place-hours-time {
  color: var(--journey-muted);
  text-align: end;
}

.journey-place-reviews {
  display: grid;
  gap: 12px;
}

.journey-place-review {
  margin: 0;
  padding: 16px 18px;
  border: 1px solid #ececef;
  border-radius: 18px;
  background: #fff;
}

.journey-place-review-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.journey-place-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--primary-background, #07a0c3);
  color: var(--primary-text-color, #fff);
  font-size: 15px;
  font-weight: 800;
}

.journey-place-review-name {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.journey-place-review-stars {
  flex-shrink: 0;
  font-size: 14px;
  letter-spacing: 1px;
  color: #f5b301;
  direction: ltr;
}

.journey-star-off {
  color: #dcdce0;
}

.journey-place-review-text {
  margin: 10px 0 0;
  padding: 0;
  border: 0;
  font-size: 15px;
  line-height: 1.65;
  color: #2b2f36;
}

@keyframes journey-place-zoom {
  from { transform: scale(1.08); }
  to { transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .journey-place-photo {
    animation: none;
  }
}
</style>

<style>
/* The popup's frame (b-modal renders it outside this component, so these
   are global, kept to the modal-class Attraction.vue sets in this design). */
.attraction-modal-journey .modal-content {
  border: 0;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 40px 90px -30px rgba(13, 17, 23, 0.55) !important;
}

.attraction-modal-journey .modal-body {
  padding: 0;
}

.attraction-modal-journey .modal-header {
  position: absolute;
  z-index: 5;
  top: 14px;
  right: 14px;
  padding: 0;
  border: 0;
}

.attraction-modal-journey .modal-header .close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.5);
  font-size: 26px;
  font-weight: 400;
  line-height: 1;
  color: #0d1117;
  text-shadow: none;
  opacity: 1;
}

.attraction-modal-journey .modal-header .close:hover,
.attraction-modal-journey .modal-header .close:focus {
  background: #fff;
  color: #0d1117;
}

.attraction-modal-journey.modal.show .modal-dialog {
  animation: journey-place-pop 0.35s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

/* b-modal puts its backdrop right after the modal. */
.attraction-modal-journey ~ .modal-backdrop {
  background: rgba(13, 17, 23, 0.55);
  opacity: 1;
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
}

@media (max-width: 575px) {
  .attraction-modal-journey .modal-dialog {
    margin: 12px;
  }
}

@keyframes journey-place-pop {
  from { opacity: 0; transform: translateY(24px) scale(0.98); }
  to { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .attraction-modal-journey.modal.show .modal-dialog {
    animation: none;
  }
}
</style>
